import { ReactNode, useCallback } from 'react';
import { Node } from 'reactflow';
import { Button } from '@givebutter/primitives/button';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { faChevronDown, faSend } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SendTestMessageDialog } from 'assets/js/pages/engage/components/SendTestMessageDialog';
import {
    DelayNodeData,
    SendEmailActionNodeFormValues,
    SendEmailNodeData,
} from 'assets/js/pages/engage/workflows/actions/types';
import { DelayBadge } from 'assets/js/pages/engage/workflows/components/DelayBadge';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useCurrentNode } from 'assets/js/pages/engage/workflows/hooks/useCurrentNode';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import { getActionNodeHeaderDetails } from 'assets/js/pages/engage/workflows/utils/getActionNodeHeaderDetails';
import { useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import * as MessageBuilder from 'components/message-builder';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { truncate } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twMerge } from 'tailwind-merge';

interface EmailMessageBuilderProps {
    selected: boolean;
    nodeId: string;
    dropdownContent: ReactNode;
}
const EmailMessageBuilder = ({ selected, nodeId, dropdownContent }: EmailMessageBuilderProps) => {
    const { accountId } = useCurrentAccount();
    const currentNode = useCurrentNode();
    const { formData, messageId } = (currentNode?.data as SendEmailNodeData) || {};
    const { updateNode } = useReactFlowStore(state => ({ updateNode: state.updateNode }));

    const methods = Form.useGivebutterForm<SendEmailActionNodeFormValues>({
        mode: 'all',
        defaultValues: {
            internal_name: formData?.values.internal_name ?? `Draft #${nodeId}`,
            body: formData?.values.body ?? '',
            subject: formData?.values.subject ?? '',
            sender_id: formData?.values.sender_id,
            message_template_id: formData?.values.message_template_id,
            body_json: formData?.values.body_json ?? '',
            editor: formData?.values.editor ?? 'basic',
        },
    });

    const { control, trigger, getValues, setValue } = methods;

    const handleOnAutosaveSuccess = useCallback(() => {
        if (messageId) {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
            });
        }
    }, [accountId, messageId]);

    useSyncFormToNode<SendEmailActionNodeFormValues>({ control, nodeId });
    useDebouncedAutosave<SendEmailActionNodeFormValues>({
        methods,
        onSuccess: handleOnAutosaveSuccess,
    });
    useTriggerValidationOnDeselection<SendEmailActionNodeFormValues>({ trigger, selected });

    const onClickDone = () => {
        if (!currentNode) return;
        updateNode(currentNode.id, {
            ...currentNode,
            selected: false,
        });
    };

    const [isPending, pendingMessageId] = useIsPending(messageId, 500);

    return pendingMessageId ? (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <div className={twMerge('hidden', selected && 'block min-w-[800px]')}>
                    <LoadingOverlay isLoading={isPending}>
                        <MessageBuilder.Root variant="email" messageId={pendingMessageId}>
                            <MessageBuilder.Header>
                                <MessageBuilder.HeaderName />
                                <MessageBuilder.HeaderActions>
                                    <SendTestMessageDialog
                                        channel={'email'}
                                        messageId={pendingMessageId}
                                    >
                                        <Button
                                            variant="outline"
                                            color="neutral"
                                            size="sm"
                                            disabled={
                                                !formData?.values.body ||
                                                !formData?.values.sender_id
                                            }
                                        >
                                            <FontAwesomeIcon icon={faSend} />
                                            Send a test
                                        </Button>
                                    </SendTestMessageDialog>
                                    <DropdownMenu.Root>
                                        <DropdownMenu.Trigger asChild>
                                            <Button variant="outline" color="neutral" size="sm">
                                                Options
                                                <FontAwesomeIcon icon={faChevronDown} />
                                            </Button>
                                        </DropdownMenu.Trigger>
                                        {dropdownContent}
                                    </DropdownMenu.Root>
                                    <Button variant="solid" size="sm" onClick={onClickDone}>
                                        Done
                                    </Button>
                                </MessageBuilder.HeaderActions>
                            </MessageBuilder.Header>
                            <MessageBuilder.Body>
                                <MessageBuilder.From />
                                <MessageBuilder.Subject />
                                <MessageBuilder.EmailFiles />
                                <MessageBuilder.Wysiwyg autoResize={false} />
                                <MessageBuilder.AdvancedEmailEditor
                                    scrollPreview
                                    onSave={(pageJson, pageHtml) => {
                                        setValue('body', pageHtml, {
                                            shouldDirty: true,
                                            shouldTouch: true,
                                            shouldValidate: true,
                                        });
                                        setValue('body_json', pageJson, {
                                            shouldDirty: true,
                                            shouldTouch: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                />
                            </MessageBuilder.Body>
                        </MessageBuilder.Root>
                    </LoadingOverlay>
                </div>
                <div className={twMerge('max-w-80', selected && 'hidden')}>
                    <SendEmailActionFormValuesDisplay values={getValues()} />
                </div>
            </StepFormContainer>
        </Form.Provider>
    ) : (
        <div className={twMerge('max-w-80', selected && 'hidden')}>
            <SendEmailActionFormValuesDisplay values={getValues()} />
        </div>
    );
};

interface SendEmailFormValuesDisplayProps {
    values: SendEmailActionNodeFormValues;
    showLabel?: boolean;
    delayNode?: Node<DelayNodeData>;
}
const SendEmailActionFormValuesDisplay = ({
    values,
    showLabel = false,
    delayNode,
}: SendEmailFormValuesDisplayProps) => {
    const { subject } = values;
    const actionHeader = getActionNodeHeaderDetails(WorkflowActionTypeEnum.SEND_EMAIL);

    const truncatedSubject = truncate(subject, {
        length: 80,
    });

    if (!actionHeader) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? (
                <p className="text-sm font-medium text-gray-900">{actionHeader.text}</p>
            ) : null}
            <div className="flex items-center gap-2">
                {delayNode && <DelayBadge delayNode={delayNode} />}
                <p className="text-sm">{truncatedSubject}</p>
            </div>
        </div>
    );
};

export { EmailMessageBuilder, SendEmailActionFormValuesDisplay };
